// client/src/pages/RedeemGift.js
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api.js';
import { useAuth } from '../hooks/useAuth.js';

// Shared card class
const cardClass = 'bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-600 rounded-2xl';

// ─── Date Formatting Helpers ──────────────────────────────

function formatLongDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatMoney(cents) {
  if (typeof cents !== 'number' || cents <= 0) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
}

// ─── Status Display Components (unchanged) ────────────────

function GiftExpired() {
  return (
    <div className="text-center py-8">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-royal-800/60 flex items-center justify-center">
        <span className="text-3xl">⏰</span>
      </div>
      <h2 className="font-display text-2xl text-royal-100 mb-2">Gift Expired</h2>
      <p className="text-royal-100 text-sm mb-6">
        This gift code is no longer valid. Gift codes have a limited redemption window.
      </p>
      <Link to="/pricing" className="text-gold hover:text-gold-light transition-colors text-sm">
        View our plans →
      </Link>
    </div>
  );
}

function GiftAlreadyRedeemed({ gift }) {
  return (
    <div className="text-center py-8">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
        <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="font-display text-2xl text-gold mb-2">Gift Already Claimed</h2>
      <p className="text-royal-100 text-sm mb-2">
        This {gift.planname} gift has already been redeemed.
      </p>
      {gift.redeemedat && (
        <p className="text-royal-100 text-xs">
          Claimed on {formatLongDate(gift.redeemedat)}
        </p>
      )}
    </div>
  );
}

function GiftPending() {
  return (
    <div className="text-center py-8">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold/10 flex items-center justify-center">
        <span className="text-3xl">✨</span>
      </div>
      <h2 className="font-display text-2xl text-gold mb-2">Gift Being Prepared</h2>
      <p className="text-royal-100 text-sm mb-6">
        The sender is still preparing your gift. Check back soon — something special is on its way!
      </p>
      <Link to="/" className="text-gold hover:text-gold-light transition-colors text-sm">
        ← Back to Home
      </Link>
    </div>
  );
}

function GiftNotFound() {
  return (
    <div className="text-center py-8">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-royal-800/60 flex items-center justify-center">
        <span className="text-3xl">🔍</span>
      </div>
      <h2 className="font-display text-2xl text-royal-100 mb-2">Gift Not Found</h2>
      <p className="text-royal-100 text-sm mb-6">
        We couldn't find a gift with that code. Please double-check the link or code you received.
      </p>
      <Link to="/" className="text-gold hover:text-gold-light transition-colors text-sm">
        ← Back to Home
      </Link>
    </div>
  );
}

function RedeemSuccess({ result }) {
  return (
    <div className="text-center py-8 space-y-6">
      <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
        <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <div>
        <h2 className="font-display text-2xl text-gold mb-2">Welcome to {result.planname}!</h2>
        <p className="text-white text-base mb-1">Your gift subscription is now active.</p>
        <p className="text-royal-100 text-sm">
          Your plan is valid until {formatLongDate(result.expiresAt)}.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/moonbeams"
          className="px-6 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors"
        >
          Start Sending eCards
        </Link>
        <Link
          to="/account"
          className="px-6 py-2.5 border border-royal-600 text-royal-100 rounded-lg text-base hover:border-royal-200 hover:text-white transition-colors"
        >
          View Account
        </Link>
      </div>
    </div>
  );
}

// ─── Scenario-driven inline copy ──────────────────────────

function buildScenarioCopy(plan, gift) {
  if (!plan) {
    return { inlineNote: null, headsUp: null, primaryLabel: 'Claim Your Gift' };
  }

  const newExpiryStr = formatLongDate(plan.newExpiry);
  const planName = gift?.planname || 'your plan';

  switch (plan.scenario) {
    case 'free':
      return {
        inlineNote: `Your gift subscription starts today and runs until ${newExpiryStr}.`,
        headsUp: null,
        primaryLabel: 'Claim Your Gift',
      };

    case 'trial':
      return {
        inlineNote: `Your free trial will end and your ${planName} gift will start today, running until ${newExpiryStr}.`,
        headsUp: null,
        primaryLabel: 'Claim Your Gift',
      };

    case 'stack_gift':
      return {
        inlineNote: `Stacks on top of your current gift. Your subscription will now run until ${newExpiryStr} instead of ${formatLongDate(plan.currentExpiry)}.`,
        headsUp: null,
        primaryLabel: 'Claim Your Gift',
      };

    case 'stack_paid_match':
      return {
        inlineNote: `Your current subscription will stop auto-renewing at the end of its billing period (no further charges after ${formatLongDate(plan.paidPeriodEnd)}). Your gift then takes over and runs until ${newExpiryStr}.`,
        headsUp: null,
        primaryLabel: 'Claim Your Gift',
      };

    case 'replace_gift': {
      const remaining = plan.remainingDays > 0
        ? `${plan.remainingDays} day${plan.remainingDays !== 1 ? 's' : ''}`
        : 'a small amount of time';
      return {
        inlineNote: null,
        headsUp: {
          title: 'Heads up — this will replace your current gift',
          body: [
            `You currently have <strong class="text-white">${plan.currentTierName}</strong>, with about <strong class="text-white">${remaining} remaining</strong> (until ${formatLongDate(plan.currentExpiry)}).`,
            `Claiming this <strong class="text-white">${planName}</strong> gift will replace it. Your current gift's remaining time will be forfeited.`,
            `Your new ${planName} subscription will run until <strong class="text-white">${newExpiryStr}</strong>.`,
          ],
        },
        primaryLabel: 'Replace and Claim',
      };
    }

    case 'switch_paid_yearly':
      return {
        inlineNote: null,
        headsUp: {
          title: 'Heads up — switching plans',
          body: [
            `You currently have <strong class="text-white">${plan.currentTierName}</strong> (yearly).`,
            `Claiming this <strong class="text-white">${planName}</strong> gift will:`,
            `<ul class="list-disc list-inside space-y-1.5 ml-1">
               <li>Cancel your current subscription immediately</li>
               <li>Refund <strong class="text-white">${formatMoney(plan.refundAmount)}</strong> for unused time (prorated)</li>
               <li>Start your ${planName} gift today, running until <strong class="text-white">${newExpiryStr}</strong></li>
             </ul>`,
            `Your auto-renewal will end. To resume ${plan.currentTierName} later, you'd need to subscribe again.`,
          ],
        },
        primaryLabel: 'Switch and Claim',
      };

    case 'switch_paid_monthly':
      return {
        inlineNote: null,
        headsUp: {
          title: 'Heads up — switching plans',
          body: [
            `You currently have <strong class="text-white">${plan.currentTierName}</strong> (monthly).`,
            `Claiming this <strong class="text-white">${planName}</strong> gift will:`,
            `<ul class="list-disc list-inside space-y-1.5 ml-1">
               <li>Cancel your current subscription immediately</li>
               <li><strong class="text-white">No refund will be issued for the current month</strong> &mdash; monthly subscriptions are non-refundable when switching via gift redemption</li>
               <li>Start your ${planName} gift today, running until <strong class="text-white">${newExpiryStr}</strong></li>
             </ul>`,
            `Your auto-renewal will end. To resume ${plan.currentTierName} later, you'd need to subscribe again.`,
          ],
        },
        primaryLabel: 'Switch and Claim',
      };

    default:
      return { inlineNote: null, headsUp: null, primaryLabel: 'Claim Your Gift' };
  }
}

// ─── Inline Heads-Up Card ────────────────────────────────
//
// Replaces the previous modal. Renders directly above the action
// buttons so the user sees the full disclosure before clicking.
//
// Styling note: uses a dark royal background in BOTH light and dark
// modes (matching the gift card's interior). The previous gold-tinted
// background was hard to read in light mode because cream/gold-dark
// text on a gold-tinted background lacked contrast. Keeping it dark
// matches the surrounding gift card panel and makes the gold border +
// gold heading the accent rather than the background tint.

function HeadsUpCard({ headsUp }) {
  if (!headsUp) return null;
  return (
    <div className="rounded-xl border border-gold/40 bg-royal-900/60 dark:bg-royal-900/40 p-4 sm:p-5 space-y-3">
      <h3 className="font-display text-base text-gold">
        {headsUp.title}
      </h3>
      <div className="space-y-3 text-royal-100 text-sm leading-relaxed">
        {headsUp.body.map((para, i) => (
          <div key={i} dangerouslySetInnerHTML={{ __html: para }} />
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────

export default function RedeemGift() {
  const { giftcode } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, loadUser } = useAuth();

  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState(null);
  const [redeemResult, setRedeemResult] = useState(null);

  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  const isLoggedIn = !!user;

  useEffect(() => {
    if (!giftcode) {
      setErrorStatus('not_found');
      setLoading(false);
      return;
    }

    api.get(`/gifts/redeem/${giftcode}`, false)
      .then((data) => setGift(data))
      .catch((err) => {
        const status = err.data?.status;
        if (status === 'expired') {
          setErrorStatus('expired');
        } else if (err.status === 404) {
          setErrorStatus('not_found');
        } else {
          setGift(err.data || null);
          setErrorStatus(err.data?.status || 'not_found');
        }
      })
      .finally(() => setLoading(false));
  }, [giftcode]);

  useEffect(() => {
    if (!isLoggedIn || !gift || gift.status !== 'available') {
      setPlan(null);
      return;
    }

    setPlanLoading(true);
    api.get(`/gifts/redeem/${giftcode}/preview`)
      .then((data) => setPlan(data))
      .catch((err) => {
        console.error('Preview error:', err);
        setPlan(null);
      })
      .finally(() => setPlanLoading(false));
  }, [isLoggedIn, gift, giftcode]);

  const doRedeem = async () => {
    if (!plan) {
      await sendRedeem(false);
      return;
    }
    await sendRedeem(!!plan.requiresConfirm);
  };

  const sendRedeem = async (confirm) => {
    setRedeeming(true);
    setRedeemError(null);

    try {
      const result = await api.post(
        `/gifts/redeem/${giftcode}`,
        confirm ? { confirm: true } : {}
      );
      setRedeemResult(result);
      if (loadUser) await loadUser();
    } catch (err) {
      if (err.status === 409 && err.data?.error === 'confirmation_required') {
        setPlan({ ...err.data.plan, requiresConfirm: true });
        setRedeemError(null);
      } else {
        setRedeemError(err.data?.error || 'Unable to redeem gift. Please try again.');
      }
    } finally {
      setRedeeming(false);
    }
  };

  const handleSaveForLater = () => {
    sessionStorage.setItem('giftToast', 'Gift saved — redeem from your email anytime.');
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (errorStatus === 'not_found') {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-12">
          <div className={`${cardClass} p-8`}><GiftNotFound /></div>
        </div>
      </div>
    );
  }

  if (errorStatus === 'expired') {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-12">
          <div className={`${cardClass} p-8`}><GiftExpired /></div>
        </div>
      </div>
    );
  }

  if (gift?.status === 'redeemed') {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-12">
          <div className={`${cardClass} p-8`}><GiftAlreadyRedeemed gift={gift} /></div>
        </div>
      </div>
    );
  }

  if (gift?.status === 'pending') {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-12">
          <div className={`${cardClass} p-8`}><GiftPending /></div>
        </div>
      </div>
    );
  }

  if (redeemResult) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-12">
          <div className={`${cardClass} p-8`}><RedeemSuccess result={redeemResult} /></div>
        </div>
      </div>
    );
  }

  const daysUntilExpiry = gift?.giftcodeexpiresat
    ? Math.max(0, Math.ceil((new Date(gift.giftcodeexpiresat) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;
  const durationLabel = gift?.giftdurationdays === 365
    ? '1 year'
    : gift?.giftdurationdays === 30 ? '1 month' : `${gift?.giftdurationdays} days`;

  const copy = buildScenarioCopy(plan, gift);
  const hasHeadsUp = !!copy.headsUp;

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-lg mx-auto px-4 py-12">
        <div className={`${cardClass} overflow-hidden`}>
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-hero-gradient opacity-80"></div>
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.12) 0%, transparent 50%)'
            }}></div>
            <div className="relative text-center py-10 px-6">
              <div className="text-5xl mb-4">🎁</div>
              <h1 className="font-display text-2xl sm:text-3xl text-gold mb-2">You've Been Gifted!</h1>
              {gift?.sendername && (
                <p className="text-white text-base">
                  <span className="text-royal-100">From</span> {gift.sendername}
                </p>
              )}
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-6">
            <div className="bg-royal-900/40 border border-royal-700/50 rounded-xl p-5 text-center">
              <p className="text-royal-100 text-sm mb-1">Your gift</p>
              <h2 className="font-display text-xl text-gold mb-1">{gift?.planname}</h2>
              {gift?.plantagline && (
                <p className="text-royal-100 text-sm mb-3">{gift.plantagline}</p>
              )}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/20 rounded-full">
                <span className="text-gold font-bold text-lg">{durationLabel}</span>
                <span className="text-royal-100 text-sm">subscription</span>
              </div>
            </div>

            {gift?.planfeatures?.length > 0 && (
              <div>
                <p className="text-royal-100 text-sm font-medium mb-2">What you'll get:</p>
                <ul className="space-y-1.5">
                  {gift.planfeatures.slice(0, 6).map((f, i) => (
                    <li key={i} className="text-sm text-white flex items-start gap-2">
                      <span className="text-gold mt-0.5 shrink-0">&#10003;</span>
                      <span>{f}</span>
                    </li>
                  ))}
                  {gift.planfeatures.length > 6 && (
                    <li className="text-sm text-royal-100">+{gift.planfeatures.length - 6} more features</li>
                  )}
                </ul>
              </div>
            )}

            {daysUntilExpiry !== null && (
              <div className="flex items-start gap-2 px-3 py-2 bg-royal-900/40 border border-royal-700/50 rounded-lg">
                <span className="text-royal-100 text-sm mt-0.5">⏱️</span>
                <p className="text-royal-100 text-sm leading-relaxed">
                  Redeem by{' '}
                  <span className="text-white font-medium">
                    {formatLongDate(gift.giftcodeexpiresat)}
                  </span>
                  {' — you have '}
                  <span className={daysUntilExpiry <= 30 ? 'text-amber-400 font-medium' : 'text-white font-medium'}>
                    {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}
                  </span>
                </p>
              </div>
            )}

            {!isLoggedIn && (
              <div className="space-y-4">
                <div className="bg-gold/10 border border-gold/20 rounded-xl p-5 text-center">
                  <p className="text-gold font-semibold text-base mb-2">Create a free account to claim your gift</p>
                  <p className="text-royal-100 text-sm mb-4">
                    You'll need an account to activate your subscription. It only takes a moment and it's completely free.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link
                      to={`/register?redirect=/gift/redeem/${giftcode}`}
                      className="px-6 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors"
                    >
                      Create Free Account
                    </Link>
                    <Link
                      to={`/login?redirect=/gift/redeem/${giftcode}`}
                      className="px-6 py-2.5 border border-royal-600 text-royal-100 rounded-lg text-base hover:border-royal-200 hover:text-white transition-colors"
                    >
                      Log In
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {isLoggedIn && planLoading && (
              <div className="flex items-center justify-center py-4">
                <div className="w-5 h-5 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
                <span className="ml-3 text-royal-100 text-sm">Checking your account...</span>
              </div>
            )}

            {isLoggedIn && !planLoading && plan && (
              <div className="space-y-4">
                {copy.inlineNote && (
                  <div className="px-4 py-3 bg-royal-900/40 border border-royal-700/50 rounded-lg">
                    <p className="text-royal-100 text-sm leading-relaxed">{copy.inlineNote}</p>
                  </div>
                )}

                {hasHeadsUp && <HeadsUpCard headsUp={copy.headsUp} />}

                {redeemError && (
                  <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
                    {redeemError}
                  </div>
                )}

                <button
                  onClick={doRedeem}
                  disabled={redeeming}
                  className="w-full py-3 bg-gold text-royal-900 font-bold rounded-xl text-lg hover:bg-gold-light transition-colors disabled:opacity-60 shadow-lg shadow-gold/20"
                >
                  {redeeming ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-royal-900 border-t-transparent rounded-full animate-spin" />
                      {hasHeadsUp ? 'Working...' : 'Claiming...'}
                    </span>
                  ) : (
                    copy.primaryLabel
                  )}
                </button>

                {hasHeadsUp && (
                  <button
                    onClick={handleSaveForLater}
                    disabled={redeeming}
                    className="w-full py-2.5 border border-royal-600 dark:border-royal-500 text-royal-100 rounded-xl text-base hover:border-gold/50 hover:text-gold transition-colors disabled:opacity-60"
                  >
                    Save for Later
                  </button>
                )}

                <p className="text-royal-100 text-xs text-center">
                  Logged in as <span className="text-white">{user.email}</span>.{' '}
                  Not you?{' '}
                  <Link to={`/login?redirect=/gift/redeem/${giftcode}`} className="text-gold hover:text-gold-light transition-colors">
                    Switch account
                  </Link>
                </p>
              </div>
            )}

            {isLoggedIn && !planLoading && !plan && (
              <div className="space-y-4">
                {redeemError && (
                  <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
                    {redeemError}
                  </div>
                )}
                <button
                  onClick={doRedeem}
                  disabled={redeeming}
                  className="w-full py-3 bg-gold text-royal-900 font-bold rounded-xl text-lg hover:bg-gold-light transition-colors disabled:opacity-60 shadow-lg shadow-gold/20"
                >
                  {redeeming ? 'Claiming...' : 'Claim Your Gift'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-royal-600 dark:text-royal-100 hover:text-gold-dark dark:hover:text-gold transition-colors text-sm">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}